import { useQuery } from '@apollo/client';
import tinycolor from 'tinycolor2';
import { minBy } from 'lodash';
import { getColorDifference } from '../utils/colors';
import { latLngToTileInfo } from '../utils/tilesets';
import { TilesetsQuery } from '../queries';

// subset of the full type -- it's just what we're using in this file
type Tileset = {
  id: string;
  name: string;
  discreteKey: {
    hexCode: string;
    label: string;
  }[];
  zoomLevels: number[];
};

const useHardinessZoneTileset = () => {
  const { data } = useQuery(TilesetsQuery);
  const tilesets: Tileset[] = data?.tilesets ?? [];
  return tilesets.find((t) => t.name === 'hardiness-2023');
};

const useGrowingZoneLookup = () => {
  const tileset = useHardinessZoneTileset();

  return async ({ lat, lng }: { lat: number; lng: number }) => {
    if (!tileset) throw new Error('Tileset not loaded');
    const { id, discreteKey, zoomLevels } = tileset;

    const { url, xOffset, yOffset } = latLngToTileInfo({
      lat,
      lng,
      zoom: Math.max(...zoomLevels),
      tilesetId: id,
    });

    // fetch the image data for the tile
    const img = new Image();
    img.src = url;
    img.crossOrigin = 'Anonymous'; // allow cross-origin fetch

    // Wait for the image to load
    const loadPromise = new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    // Give up after 3 seconds
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => {
        reject(new Error('Image load timed out'));
      }, 3000),
    );
    await Promise.race([loadPromise, timeoutPromise]).catch(() => {
      throw new Error('Cannot load tileset image');
    });

    // draw the image to a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2d context');
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0);

    // NOTE: the [xy]Offset values are based on a bottom-left origin but JS Context uses a top-left origin, so we subtract yOffset from 1
    // Also, the max pixel values in each direction are `width - 1` and `height - 1`, since they start at 0 in each dimension
    const pixelX = Math.floor(xOffset * (img.width - 1));
    const pixelY = Math.floor((1 - yOffset) * (img.height - 1));

    // fetch the color of the pixel at the offsets
    const pixelData = ctx.getImageData(pixelX, pixelY, 1, 1).data;
    const [r, g, b] = pixelData.slice(0, 3);
    const color = tinycolor({ r, g, b }).toHexString();

    // get closest color from the key
    const match = minBy(discreteKey, (k) =>
      getColorDifference(color, k.hexCode),
    );

    // The color difference is on a scale from 0 to 100.
    // Most of the time it should be 0, but if we happen to have sampled a point
    // that's right on the boundary between two growing zones, the sampled color
    // might be in-between two of the actual colors in the key.
    // In this case, we pick the closest color. In practice, the difference is
    // almost always less than 10, so if it's greater than 10, there's probably
    // been some issue.
    if (!match || getColorDifference(color, match.hexCode) > 10) {
      throw new Error('No match found for growing zone color lookup');
    }

    return match.label;
  };
};

export default useGrowingZoneLookup;
